import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackErrorEvent } from '@/helpers/analytics';
import { notifyError } from '@/helpers/errorReporting';
import { errorCopy, getErrorCopy } from '@/Pages/Error/copy';
import {
  parseErrorLocationState,
  type ErrorLocationState,
  type ErrorType,
} from '@/Pages/Error/types/errorLocationState';

export type ErrorPageViewModel = {
  errorType: ErrorType;
  title: string;
  description: string;
  errorCode?: string;
  showRetry: boolean;
  supportUrl: string;
};

export type UseErrorPageResult = ErrorPageViewModel & {
  isRetrying: boolean;
  handleRetry: () => void;
  handleHome: () => void;
  handleSupport: () => void;
};

function buildViewModel(state: ErrorLocationState): ErrorPageViewModel {
  const copy = getErrorCopy(state.errorType);

  return {
    errorType: state.errorType,
    title: copy.title,
    description: state.errorMessage ?? copy.description,
    ...(state.errorCode !== undefined ? { errorCode: state.errorCode } : {}),
    showRetry: state.canRetry === true,
    supportUrl: errorCopy.supportUrl,
  };
}

export function useErrorPage(): UseErrorPageResult {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRetrying, setIsRetrying] = useState(false);
  const hasTrackedView = useRef(false);

  const locationState = useMemo(
    () => parseErrorLocationState(location.state),
    [location.state],
  );

  const viewModel = useMemo(() => buildViewModel(locationState), [locationState]);

  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;

    void trackErrorEvent({
      name: 'error_page_view',
      params: {
        error_type: locationState.errorType,
        ...(locationState.errorCode !== undefined
          ? { error_code: locationState.errorCode }
          : {}),
        ...(locationState.originPath !== undefined
          ? { origin_path: locationState.originPath }
          : {}),
      },
    });

    notifyError({
      errorType: locationState.errorType,
      ...(locationState.errorCode !== undefined
        ? { errorCode: locationState.errorCode }
        : {}),
      ...(locationState.originPath !== undefined
        ? { originPath: locationState.originPath }
        : {}),
    });
  }, [locationState]);

  const handleRetry = useCallback(() => {
    setIsRetrying(true);

    void trackErrorEvent({
      name: 'error_retry_click',
      params: {
        error_type: locationState.errorType,
        ...(locationState.errorCode !== undefined
          ? { error_code: locationState.errorCode }
          : {}),
      },
    });

    if (locationState.originPath) {
      navigate(locationState.originPath, {
        state: locationState.originState,
      });
      return;
    }

    navigate(-1);
  }, [locationState, navigate]);

  const handleHome = useCallback(() => {
    void trackErrorEvent({
      name: 'error_home_click',
      params: { error_type: locationState.errorType },
    });
    navigate('/welcome', { replace: true });
  }, [locationState.errorType, navigate]);

  const handleSupport = useCallback(() => {
    void trackErrorEvent({
      name: 'error_support_click',
      params: { error_type: locationState.errorType },
    });
    window.open(errorCopy.supportUrl, '_blank', 'noopener,noreferrer');
  }, [locationState.errorType]);

  return {
    ...viewModel,
    isRetrying,
    handleRetry,
    handleHome,
    handleSupport,
  };
}
